import tool from 'common/src/modules/shared/copilot/utils/creators/tool';
import {V2CardAgentToolName} from '../../promptToCardAgentV2.enums';
import {z} from 'zod';
import {ToolExecuteParams, ToolResult} from 'common/src/modules/shared/copilot/copilot.types';
import toolResult from 'common/src/modules/shared/copilot/utils/creators/toolResult';
import toolError from 'common/src/modules/shared/copilot/utils/toolError';
import {ContentCardScratchpadState} from '../../scratchpad/contentCardScratchpad.types';
import {ProductMatchingOperator} from 'common/src/modules/decisionTree/decisionTree.types';
import validateAgentScratchpad from '../../utils/general/validateAgentScratchpad';
import {NodeType} from 'common/src/modules/nodes/nodes.types';
import {MacroItemTypeValue} from 'common/src/modules/macros/macros.types';
import {CLStore} from 'common/src/redux/store';
import {ContentSelectionStrategyValue} from 'common/src/modules/contentFeeds/contentFeeds.enums';

const setMatchingConditionsToolSchema = z.object({
  conditions: z
    .array(
      z.object({
        feedColumn: z.string().describe('The field to match on'),
        productMatchingOperator: z
          .nativeEnum(ProductMatchingOperator)
          .describe(
            'The operator to use for matching (1 - Equals, 2 - Includes, 3 - Included in, 4 - Less than or equal, 5 - Greater than or equal)',
          ),
        signalKey: z.string().describe('The signal to match against'),
      }),
    )
    .describe('Array of matching conditions'),
});

export default function setMatchingConditionsTool(store: CLStore) {
  async function execute({
    input: {conditions},
    toolUtils: {agentScratchpad},
  }: ToolExecuteParams<
    typeof setMatchingConditionsToolSchema,
    ContentCardScratchpadState
  >): Promise<ToolResult> {
    const validatedScratchpad = validateAgentScratchpad<ContentCardScratchpadState>(
      agentScratchpad,
      NodeType.CONTENT_FEED,
    );

    const {campaign, versionId} = store.getState().campaignPage.campaignTree;

    if (!campaign) {
      return toolError('Campaign is required to set matching conditions');
    }

    if (!versionId) {
      return toolError('Version ID is required to set matching conditions');
    }

    if (!validatedScratchpad.card.contentFeedId) {
      return toolError('No feed ID found, you need to set a feed first');
    }

    if (!validatedScratchpad.card.contentFeedId) {
      return toolError('Feed is required to set matching conditions');
    }

    if (validatedScratchpad.card.strategy !== ContentSelectionStrategyValue.Matching) {
      return toolError('Content selection strategy is not matching');
    }

    const conditionSignals = validatedScratchpad.card.availableSignals;

    if (!conditionSignals || conditionSignals.length === 0) {
      return toolError('No signals found, you need to search for signals first');
    }

    const columns = validatedScratchpad.card.availableFeedColumns;

    if (!columns || columns.length === 0) {
      return toolError('No feed columns found, you need to search for feed columns first');
    }

    const validatedConditions = conditions.map((condition, index) => {
      const feedColumn = columns.find((column) => column.value === condition.feedColumn);

      const signal = conditionSignals?.find((signal) => signal.signalKey === condition.signalKey);
      const conditionErrors: string[] = [];

      if (!feedColumn) {
        conditionErrors.push(
          `Feed column ${condition.feedColumn} is not available at index ${index}`,
        );
      }

      if (!signal) {
        conditionErrors.push(`Signal ${condition.signalKey} is not available at index ${index}`);
      }

      if (
        feedColumn?.type === MacroItemTypeValue.Numeric ||
        feedColumn?.type === MacroItemTypeValue.Date
      ) {
        if (
          ![
            ProductMatchingOperator.GreaterThanOrEqual,
            ProductMatchingOperator.LessThanOrEqual,
          ].includes(condition.productMatchingOperator)
        ) {
          conditionErrors.push(
            `Operator ${condition.productMatchingOperator} is not available for feed column ${condition.feedColumn} at index ${index}`,
          );
        }
      }

      if (
        feedColumn?.type !== MacroItemTypeValue.Numeric &&
        feedColumn?.type !== MacroItemTypeValue.Date
      ) {
        if (
          ![
            ProductMatchingOperator.Equals,
            ProductMatchingOperator.Contains,
            ProductMatchingOperator.ContainedIn,
          ].includes(condition.productMatchingOperator)
        ) {
          conditionErrors.push(
            `Operator ${condition.productMatchingOperator} is not available for feed column ${condition.feedColumn} at index ${index}`,
          );
        }
      }

      return {
        ...condition,
        errors: conditionErrors,
      };
    });

    const invalidConditions = validatedConditions.filter(
      (condition) => condition.errors.length > 0,
    );
    const validConditions = validatedConditions.filter(
      (condition) => condition.errors.length === 0,
    );

    if (validConditions.length === 0) {
      return toolError(
        JSON.stringify({
          status: 'Error',
          message:
            'At least one condition is required to set matching conditions, and the non of the conditions are valid',
          errors: invalidConditions.map((condition) => condition.errors.join('\n')),
        }),
      );
    }

    validatedScratchpad.card.configuration = {
      conditions: validConditions.map((condition) => ({
        feedColumnSignalKey: condition.feedColumn,
        operator: condition.productMatchingOperator as ProductMatchingOperator,
        signalKey: condition.signalKey,
        feedColumnUIName: '',
        signalUIName: '',
      })),
      orderBy: 0,
      contentType: 0,
    };

    if (invalidConditions.length > 0) {
      return toolResult(
        JSON.stringify({
          status: 'success',
          message: `Only some of the conditions were set because of the following errors: ${invalidConditions
            .map((condition) => condition.errors.join('\n'))
            .join('\n')}. The card can be created, with the valid conditions only`,
        }),
      );
    }

    return toolResult(
      JSON.stringify({
        status: 'success',
        message: `Matching conditions set: ${conditions.length} conditions configured`,
      }),
    );
  }

  return tool({
    name: V2CardAgentToolName.SetMatchingConditions,
    displayName: 'Setting conditions',
    description: 'Set matching conditions for content selection. Only for the Matching strategy.',
    inputSchema: setMatchingConditionsToolSchema,
    execute,
  });
}
